
import { useEffect, useState } from "react";
import { useOrchestration } from "@/hooks/useOrchestration";
import { ChatHeader } from "./ChatHeader";
import { ChatMessageList } from "./ChatMessageList";
import { ChatInput } from "./ChatInput";
import { AgentOrchestratorStatus } from "./AgentOrchestratorStatus";
import { Message } from "@/components/ai/types";
import { Separator } from "@/components/ui/separator";
import { logErrorToast } from "@/utils/toastLogger";
import { sendMessageToOrchestrator } from "@/services/orchestratorService";

interface ChatModalProps {
  isPopout?: boolean;
}

export const ChatModal = ({ isPopout = false }: ChatModalProps) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content: "Hello! I'm BrigAI, your executive assistant. I'm powered by real AI models and ready to help you with recruitment, scheduling, and coordinating tasks. How can I assist you today?",
      sender: "ai",
      timestamp: new Date(),
    },
  ]);
  
  const {
    orchestrationActive,
    activeAgents,
    taskComplexity,
    orchestrationInsights,
    processUserInput,
    availableAgents,
    pendingClarification
  } = useOrchestration();

  useEffect(() => {
    console.log("Orchestration active:", orchestrationActive);
    console.log("Active agents:", activeAgents);
  }, [orchestrationActive, activeAgents]);

  const handleSendMessage = async (content: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setShowTypingIndicator(true);
    
    try {
      console.log('Sending message to real AI via orchestrator...');
      
      // Send directly to orchestrator for real AI processing
      const aiResponse = await sendMessageToOrchestrator({
        content,
        targetAgent: 'executive-agent'
      });
      
      const aiMessage: Message = {
        id: aiResponse.id,
        content: aiResponse.content,
        sender: 'ai',
        timestamp: aiResponse.timestamp
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
    } catch (error) {
      console.error("Error processing message:", error);
      logErrorToast("Failed to process your message", error);
      
      // Final fallback response
      const errorMessage: Message = {
        id: (Date.now() + 100).toString(),
        content: "I'm sorry, I encountered an issue processing your request. Please check that your API keys are configured in Settings > Integrations, or try again later.",
        sender: "ai",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setTimeout(() => {
        setShowTypingIndicator(false);
      }, 500);
    }
  };

  // Show typing indicator when orchestrating agents or waiting for clarification
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  
  useEffect(() => {
    if (orchestrationActive) {
      setShowTypingIndicator(true);
    } else {
      const timeout = setTimeout(() => {
        setShowTypingIndicator(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [orchestrationActive]);

  // Determine if the agent orchestration panel should be visible
  const showOrchestrationPanel = orchestrationActive || pendingClarification;

  return (
    <div className={`flex flex-col ${isPopout ? 'h-screen' : 'h-full'} bg-background`}>
      <ChatHeader 
        orchestrationActive={orchestrationActive} 
        complexity={taskComplexity} 
      />
      
      <div className="flex-1 flex overflow-hidden">
        <div className={`flex-1 flex flex-col h-full overflow-hidden ${showOrchestrationPanel ? 'max-w-[65%]' : 'w-full'}`}>
          <ChatMessageList 
            messages={messages} 
            showTypingIndicator={showTypingIndicator}
          />
          
          <Separator />
          
          <ChatInput 
            onSendMessage={handleSendMessage}
            disabled={showTypingIndicator}
          />
        </div>
        
        <AgentOrchestratorStatus 
          activeAgents={activeAgents}
          availableAgents={availableAgents}
          insights={orchestrationInsights}
          className={showOrchestrationPanel ? 'flex' : 'hidden'}
        />
      </div>
    </div>
  );
}
